/**
 * Offline Queue - Queue mutations when offline, sync when online
 * Supports: retry, conflict handling (last_updated_at)
 */
import { initDB } from './offlineDb';

const QUEUE_STORE_NAME = 'request-queue';
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

const generateQueueId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// URLs that must NEVER be auto-replayed from the offline queue. Re-sending a
// login/logout can clobber the active session; re-sending a payment endpoint
// (pos/payments + .../confirm|refund|void, .../pay, .../pay-deposit,
// vouchers/payment) can double-charge. NOTE: `accounting/payment-methods` is
// config CRUD (not a charge) so it is intentionally NOT matched and still
// queues normally — the `-methods` suffix prevents the `payment` segment match.
const NON_QUEUEABLE_URL_PATTERN =
  /(^|\/)(auth|payments?|pay|pay-deposit)(\/|$|\?)/i;

// FormData can't be structured-cloned into IndexedDB (throws DataCloneError),
// so a request with a FormData body cannot be persisted in the queue.
const isFormDataBody = (data) =>
  typeof FormData !== 'undefined' && data instanceof FormData;

/**
 * Whether a request is safe to persist in the offline queue for later replay.
 * Excludes FormData bodies (not serializable into IndexedDB) and auth/payment
 * endpoints (dangerous to auto-replay). apiRequest checks this BEFORE trying to
 * queue, so the original network error surfaces instead of a false
 * "saved offline" success.
 */
export const isQueueableRequest = ({ url, data } = {}) => {
  if (isFormDataBody(data)) return false;
  if (typeof url === 'string' && NON_QUEUEABLE_URL_PATTERN.test(url)) return false;
  return true;
};

/**
 * Add request to offline queue
 */
export const queueRequest = async (requestData) => {
  // Defensive guard: never attempt to store a FormData body — IndexedDB cannot
  // structured-clone it and `store.add` would throw DataCloneError. Callers
  // should gate on isQueueableRequest first; this is belt-and-braces.
  if (isFormDataBody(requestData?.data)) {
    console.warn('[Offline Queue] Skipping FormData request (cannot be cloned):', requestData?.url);
    return null;
  }
  try {
    const database = await initDB();
    const transaction = database.transaction([QUEUE_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(QUEUE_STORE_NAME);
    const id = generateQueueId();
    const item = {
      id,
      url: requestData.url,
      fullUrl: requestData.fullUrl,
      method: requestData.method,
      data: requestData.data,
      headers: requestData.headers || {},
      entity: requestData.entity,
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
      lastError: null,
    };
    await store.add(item);
    return id;
  } catch (error) {
    console.error('[Offline Queue] Error queueing:', error);
    throw error;
  }
};

/**
 * Get all pending requests sorted by timestamp
 */
export const getPendingRequests = async () => {
  try {
    const database = await initDB();
    const transaction = database.transaction([QUEUE_STORE_NAME], 'readonly');
    const store = transaction.objectStore(QUEUE_STORE_NAME);
    const index = store.index('status');
    const all = await new Promise((resolve, reject) => {
      const req = index.getAll(IDBKeyRange.only('pending'));
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    return all.sort((a, b) => a.timestamp - b.timestamp);
  } catch (error) {
    console.error('[Offline Queue] Error getting pending:', error);
    return [];
  }
};

/**
 * Get count of pending requests
 */
export const getPendingCount = async () => {
  const pending = await getPendingRequests();
  return pending.length;
};

/**
 * Update queue item status
 */
export const updateQueueItemStatus = async (id, status, result = null, lastError = null) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([QUEUE_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(QUEUE_STORE_NAME);
    const item = await new Promise((resolve, reject) => {
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    if (item) {
      item.status = status;
      if (result) item.result = result;
      if (lastError) item.lastError = lastError;
      if (status === 'processing') item.retryCount = (item.retryCount || 0) + 1;
      await store.put(item);
    }
  } catch (error) {
    console.error('[Offline Queue] Error updating status:', error);
  }
};

/**
 * Remove queue item after successful sync
 */
export const removeQueueItem = async (id) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([QUEUE_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(QUEUE_STORE_NAME);
    await store.delete(id);
  } catch (error) {
    console.error('[Offline Queue] Error removing:', error);
  }
};

/**
 * Reset failed items to pending for retry
 */
export const resetFailedToPending = async () => {
  try {
    const database = await initDB();
    const transaction = database.transaction([QUEUE_STORE_NAME], 'readwrite');
    const store = transaction.objectStore(QUEUE_STORE_NAME);
    const all = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    for (const item of all) {
      if (item.status === 'failed' && (item.retryCount || 0) < MAX_RETRIES) {
        item.status = 'pending';
        item.lastError = null;
        await store.put(item);
      }
    }
  } catch (error) {
    console.error('[Offline Queue] Error resetting failed:', error);
  }
};

/**
 * Process pending requests when online
 * Uses axios directly to avoid circular deps with apiRequest
 */
export const processPendingRequests = async (dispatch, apiRequest) => {
  if (!navigator.onLine) return { processed: 0, failed: 0 };

  const pending = await getPendingRequests();
  if (pending.length === 0) return { processed: 0, failed: 0 };

  let processed = 0;
  let failed = 0;

  for (const req of pending) {
    await updateQueueItemStatus(req.id, 'processing');
    await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));

    try {
      const result = await dispatch(
        apiRequest({
          entity: req.entity,
          url: req.url,
          method: req.method,
          data: req.data,
          headers: req.headers,
          showSuccessToast: false,
          showErrorToast: false,
        })
      ).unwrap();

      if (result?.success !== false) {
        await removeQueueItem(req.id);
        processed++;
      } else {
        await updateQueueItemStatus(req.id, 'failed', null, result?.error);
        failed++;
      }
    } catch (error) {
      const msg = error?.message || error?.error || String(error);
      await updateQueueItemStatus(req.id, 'failed', null, msg);
      failed++;
    }
  }

  return { processed, failed };
};
